import { useState, useCallback, useEffect } from 'react'
import * as adminApi from '../services/adminApi.js'
import type { PlanRecord } from '../types/index.js'

export function useAdminPlans() {
  const [plans, setPlans] = useState<PlanRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminApi.listPlans()
      setPlans(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchPlans()
  }, [fetchPlans])

  const createPlan = useCallback(
    async (input: { name: string; seats: number; monthlyAiCredits: number }) => {
      const created = await adminApi.createPlan(input)
      setPlans((prev) => [...prev, created])
      return created
    },
    [],
  )

  const updatePlan = useCallback(
    async (id: string, input: Partial<{ name: string; seats: number; monthlyAiCredits: number }>) => {
      const updated = await adminApi.updatePlan(id, input)
      setPlans((prev) => prev.map((p) => (p.id === id ? updated : p)))
      return updated
    },
    [],
  )

  const deletePlan = useCallback(async (id: string) => {
    await adminApi.deletePlan(id)
    setPlans((prev) => prev.filter((p) => p.id !== id))
  }, [])

  return { plans, loading, error, refetch: fetchPlans, createPlan, updatePlan, deletePlan }
}
